import type { WallOpening } from '../framing/generateFramedWall';
import type { CutRow, MaterialRow } from '../../engines/types';

const EXTERIOR_TRIM_WIDTH_MM = 40;
const EXTERIOR_TRIM_DIMENSION = '22×45 mm';
const EXTERIOR_TRIM_PRICE_RULE_ID = 'prices.fonsterfoder';

function isExteriorOpening(opening: WallOpening): boolean {
  return opening.wall !== 'divider';
}

function getTrimLabel(type: WallOpening['type']): string {
  return type === 'door' ? 'Dörrfoder' : 'Fönsterfoder';
}

function getHorizontalPieceCount(type: WallOpening['type']): number {
  return type === 'door' ? 1 : 2;
}

function getVerticalPieceLengthMm(opening: WallOpening): number {
  return opening.heightMm + EXTERIOR_TRIM_WIDTH_MM * 2;
}

function getOpeningTrimLengthMm(opening: WallOpening): number {
  const verticalLengthMm = getVerticalPieceLengthMm(opening) * 2;
  const horizontalLengthMm = opening.widthMm * getHorizontalPieceCount(opening.type);
  return verticalLengthMm + horizontalLengthMm;
}

export function calculateOpeningTrimTakeoff(openings: WallOpening[]): {
  materials: MaterialRow[];
  cuts: CutRow[];
} {
  const exteriorOpenings = openings.filter(isExteriorOpening);
  if (exteriorOpenings.length === 0) {
    return { materials: [], cuts: [] };
  }

  const totalsByType = new Map<WallOpening['type'], { count: number; totalLengthMm: number }>();
  const cuts: CutRow[] = [];

  for (const opening of exteriorOpenings) {
    const trimLabel = getTrimLabel(opening.type);
    const verticalPieceLengthMm = getVerticalPieceLengthMm(opening);
    const horizontalPieceCount = getHorizontalPieceCount(opening.type);
    const totalLengthMm = getOpeningTrimLengthMm(opening);
    const currentTotals = totalsByType.get(opening.type) ?? { count: 0, totalLengthMm: 0 };

    totalsByType.set(opening.type, {
      count: currentTotals.count + 1,
      totalLengthMm: currentTotals.totalLengthMm + totalLengthMm,
    });

    cuts.push({
      part: `${trimLabel} (${opening.wall})`,
      count: '1 sats',
      cutLength: `2 × ${verticalPieceLengthMm} mm, ${horizontalPieceCount} × ${opening.widthMm} mm`,
      materialKey: `exterior.opening-trim.${opening.type}`,
    });
  }

  const materials: MaterialRow[] = [...totalsByType.entries()].map(([type, total]) => ({
    material: getTrimLabel(type),
    dimension: EXTERIOR_TRIM_DIMENSION,
    quantity: `${total.count} sats = ${(total.totalLengthMm / 1000).toFixed(2)} lpm`,
    phase: 'roofExterior',
    priceRuleId: EXTERIOR_TRIM_PRICE_RULE_ID,
    materialKey: `exterior.opening-trim.${type}`,
  }));

  return { materials, cuts };
}
